// SYSTEM_ROUTE: /environment
import { useState } from 'react';
import type { InventoryItem, ItemCategory, ItemStatus } from '@life-os/app-core';
import { ITEM_CATEGORIES, ITEM_STATUSES, summarizeInventory } from '@life-os/app-core';
import { Button, Chip, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const STATUS_TONE: Record<ItemStatus, ChipTone> = {
  in_use: 'primary',
  storage: 'muted',
  repair: 'warn',
  retired: 'muted',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

export function EnvironmentScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ItemCategory>('device');
  const [location, setLocation] = useState('');

  const items = useAsync<readonly InventoryItem[]>(() => services.environment.list(), [revision]);
  const list = items.value ?? [];
  const summary = summarizeInventory(list);

  const add = async () => {
    if (name.trim().length === 0) return;
    await services.environment.create({ name, category, location });
    setName('');
    setLocation('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Environment & Devices</>} subtitle={<>Your workspace, devices, tools, and inventory — so attention isn't lost to "where is it" or
            "what state is it in".</>} actions={<>{summary.needsAttention > 0 ? (
          <div className="lc-row__meta">
            <Chip tone="warn">{summary.needsAttention} in repair</Chip>
          </div>
        ) : null}</>} />

      <Pane title="Inventory" hint={`${summary.inUse} in use of ${summary.total}`}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ITEM_CATEGORIES.map((c) => (
            <Chip key={c} tone={summary.byCategory[c] > 0 ? 'neutral' : 'muted'}>
              {c} {summary.byCategory[c] > 0 ? summary.byCategory[c] : ''}
            </Chip>
          ))}
        </div>
      </Pane>

      <Pane title="Add an item">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 180px' }}
            value={name}
            placeholder="Item name…"
            onChange={(e) => setName(e.target.value)}
            aria-label="Item name"
          />
          <select value={category} onChange={(e) => setCategory(e.target.value as ItemCategory)} aria-label="Category" style={inputStyle}>
            {ITEM_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 140px' }}
            value={location}
            placeholder="Location"
            onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            aria-label="Location"
          />
          <Button variant="primary" onClick={() => void add()}>
            Add
          </Button>
        </div>
      </Pane>

      <Pane title="Items" hint={`${list.length}`}>
        <Stack>
          {list.length === 0 ? (
            <EmptyState title="No items yet">Add the devices, tools, and spaces you rely on, and track their state.</EmptyState>
          ) : (
            list.map((i) => (
              <div key={String(i.id)} className="lc-row" style={{ alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <strong>{i.name}</strong>
                  <span style={{ color: 'var(--lc-on-surface-variant)' }}>
                    {' · '}
                    {i.category}
                    {i.location ? ` · ${i.location}` : ''}
                  </span>
                </div>
                <Chip tone={STATUS_TONE[i.status]}>{i.status.replace('_', ' ')}</Chip>
                <select
                  value={i.status}
                  onChange={(e) => void services.environment.setStatus(i.id, e.target.value as ItemStatus).then(refresh)}
                  aria-label="Item status"
                  style={inputStyle}
                >
                  {ITEM_STATUSES.map((st) => (
                    <option key={st} value={st}>
                      {st.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
